import { createBusinessId } from '@/businessRepository/idUtils';
import {
  IUnmanagedBusiness,
  IUnmanagedBusinessTypeAnnounce,
  IUnmanagedBusinessTypeArrival,
  IUnmanagedBusinessTypeInventory,
  IUnmanagedBusinessTypeSalesResult,
  ImportFileType,
} from '../entities/unmanagedBusiness';
import { DateService } from '../services/dateService';

/**
 * 未管理取引のファクトリークラス
 */
export class UnmanagedBusinessFactory {
  /**
   * IUnmanagedBusinessを生成する
   * @param initialData - 初期値
   */
  public static create = (initialData: Partial<IUnmanagedBusiness> & { importFileType: ImportFileType }): IUnmanagedBusiness => {
    switch (initialData.importFileType) {
      case ImportFileType.Announce:
        const announce: IUnmanagedBusiness & Required<IUnmanagedBusinessTypeAnnounce> = {
          businessId: initialData.businessId ?? createBusinessId(),
          baseBusinessId: initialData.baseBusinessId ?? '',
          aggregateBusinessId: initialData.aggregateBusinessId ?? '',
          businessNumber: initialData.businessNumber ?? '',
          businessName: initialData.businessName ?? '',
          warehouse: initialData.warehouse ?? '',
          package: initialData.package ?? '',
          fileName: initialData.fileName ?? '',
          // ImportFileTypeは必ず指定する必要がある
          importFileType: initialData.importFileType,
          // 入力日は指定がない場合は今日の日付を設定する
          inputDate: initialData.inputDate ?? DateService.convertDateToIDate(new Date()),
          announce: initialData.announce ?? [],
          customerName: initialData.customerName ?? '',
          salesDestinationName: initialData.salesDestinationName ?? '',
          supplierName: initialData.supplierName ?? '',
          stdInventoryIndices: initialData.stdInventoryIndices ?? ([] as unknown as IUnmanagedBusiness['stdInventoryIndices']),
          supplierNumber: initialData.supplierNumber ?? '',
          warehouseNumber: initialData.warehouseNumber ?? '',
          moq: initialData.moq ?? 0,
          leadTime: initialData.leadTime ?? 0,
          leadTimeUnit: initialData.leadTimeUnit ?? '',
          customerNumber: initialData.customerNumber ?? '',
          salesDestinationNumber: initialData.salesDestinationNumber ?? '',
          unit: initialData.unit ?? '',
          executionDate: initialData.executionDate ?? DateService.convertDateToIDate(new Date(1000)),
        };
        return announce;
      case ImportFileType.SalesResult:
        const salesResult: IUnmanagedBusiness & Required<IUnmanagedBusinessTypeSalesResult> = {
          businessId: initialData.businessId ?? createBusinessId(),
          baseBusinessId: initialData.baseBusinessId ?? '',
          aggregateBusinessId: initialData.aggregateBusinessId ?? '',
          businessNumber: initialData.businessNumber ?? '',
          businessName: initialData.businessName ?? '',
          warehouse: initialData.warehouse ?? '',
          package: initialData.package ?? '',
          fileName: initialData.fileName ?? '',
          // ImportFileTypeは必ず指定する必要がある
          importFileType: initialData.importFileType,
          // 入力日は指定がない場合は今日の日付を設定する
          inputDate: initialData.inputDate ?? DateService.convertDateToIDate(new Date()),
          salesResults: initialData.salesResults ?? [],
          customerName: initialData.customerName ?? '',
          salesDestinationName: initialData.salesDestinationName ?? '',
          supplierName: initialData.supplierName ?? '',
          stdInventoryIndices: initialData.stdInventoryIndices ?? ([] as unknown as IUnmanagedBusiness['stdInventoryIndices']),
          supplierNumber: initialData.supplierNumber ?? '',
          warehouseNumber: initialData.warehouseNumber ?? '',
          moq: initialData.moq ?? 0,
          leadTime: initialData.leadTime ?? 0,
          leadTimeUnit: initialData.leadTimeUnit ?? '',
          customerNumber: initialData.customerNumber ?? '',
          salesDestinationNumber: initialData.salesDestinationNumber ?? '',
          unit: initialData.unit ?? '',
          executionDate: initialData.executionDate ?? DateService.convertDateToIDate(new Date(1000)),
          endUser: initialData.endUser ?? undefined,
        };
        return salesResult;
      case ImportFileType.Inventory:
        const inventory: IUnmanagedBusiness & Required<IUnmanagedBusinessTypeInventory> = {
          businessId: initialData.businessId ?? createBusinessId(),
          baseBusinessId: initialData.baseBusinessId ?? '',
          aggregateBusinessId: initialData.aggregateBusinessId ?? '',
          businessNumber: initialData.businessNumber ?? '',
          businessName: initialData.businessName ?? '',
          warehouse: initialData.warehouse ?? '',
          package: initialData.package ?? '',
          fileName: initialData.fileName ?? '',
          // ImportFileTypeは必ず指定する必要がある
          importFileType: initialData.importFileType,
          // 入力日は指定がない場合は今日の日付を設定する
          inputDate: initialData.inputDate ?? DateService.convertDateToIDate(new Date()),
          inventory: initialData.inventory ?? 0,
          supplierName: initialData.supplierName ?? '',
          stdInventoryIndices: initialData.stdInventoryIndices ?? ([] as unknown as IUnmanagedBusiness['stdInventoryIndices']),
          supplierNumber: initialData.supplierNumber ?? '',
          warehouseNumber: initialData.warehouseNumber ?? '',
          moq: initialData.moq ?? 0,
          leadTime: initialData.leadTime ?? 0,
          leadTimeUnit: initialData.leadTimeUnit ?? '',
          customerNumber: initialData.customerNumber ?? '',
          salesDestinationNumber: initialData.salesDestinationNumber ?? '',
          unit: initialData.unit ?? '',
          inventories: initialData.inventories ?? [],
          executionDate: initialData.executionDate ?? DateService.convertDateToIDate(new Date(1000)),
          manufacturer: initialData.manufacturer ?? undefined,
        };
        return inventory;
      case ImportFileType.Arrival:
        const arrival: IUnmanagedBusiness & Required<IUnmanagedBusinessTypeArrival> = {
          businessId: initialData.businessId ?? createBusinessId(),
          baseBusinessId: initialData.baseBusinessId ?? '',
          aggregateBusinessId: initialData.aggregateBusinessId ?? '',
          businessNumber: initialData.businessNumber ?? '',
          businessName: initialData.businessName ?? '',
          warehouse: initialData.warehouse ?? '',
          package: initialData.package ?? '',
          fileName: initialData.fileName ?? '',
          // ImportFileTypeは必ず指定する必要がある
          importFileType: initialData.importFileType,
          // 入力日は指定がない場合は今日の日付を設定する
          inputDate: initialData.inputDate ?? DateService.convertDateToIDate(new Date()),
          arrival: initialData.arrival ?? 0,
          supplierName: initialData.supplierName ?? '',
          stdInventoryIndices: initialData.stdInventoryIndices ?? ([] as unknown as IUnmanagedBusiness['stdInventoryIndices']),
          supplierNumber: initialData.supplierNumber ?? '',
          warehouseNumber: initialData.warehouseNumber ?? '',
          moq: initialData.moq ?? 0,
          leadTime: initialData.leadTime ?? 0,
          leadTimeUnit: initialData.leadTimeUnit ?? '',
          customerNumber: initialData.customerNumber ?? '',
          salesDestinationNumber: initialData.salesDestinationNumber ?? '',
          unit: initialData.unit ?? '',
          arrivals: initialData.arrivals ?? [],
          executionDate: initialData.executionDate ?? DateService.convertDateToIDate(new Date(1000)),
          manufacturer: initialData.manufacturer ?? undefined,
        };
        return arrival;
    }
  };
}
